import { GridLogicOperator } from '@mui/x-data-grid-premium'
import { first } from 'lodash'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '~/@/components/ui/button'
import { cn } from '~/@/lib/utils'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getGridFilterOperators } from '~/common/utils/grid-filters'
import { ActiveUser } from '~/components/settings/types'
import { DossGridFilterItem, DossGridFilterModel } from '~/components/tables/filters'
import { DetailedTable } from '~/components/tables/types'
import { canDuplicateFilter, FilterRow } from './CustomFilterPanelRow'
import { TableColumnOption } from './TableColumnAutocomplete'
interface FilterPanelProps {
  columns: TableColumnOption[]
  table: DetailedTable
  filterModel: DossGridFilterModel
  onFilterModelChange: (model: DossGridFilterModel) => void
  pendingFilterItem: DossGridFilterItem | null
  activeUsers?: ActiveUser[]
  tableId: string
  readOnly: boolean
  /** When true, allow editing if the user can view the table (e.g. ad-hoc lookup refinement). */
  lookupRefinementMode?: boolean
}

export function CustomFilterPanel({
  columns,
  filterModel,
  onFilterModelChange,
  pendingFilterItem,
  activeUsers,
  tableId,
  readOnly,
  table,
  lookupRefinementMode = false,
}: FilterPanelProps) {
  const { t } = useTypedTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  const { permissionsManager } = useGlobalContext()
  const [hasInitializedDraftFilterModel, setHasInitializedDraftFilterModel] = useState(false)

  const canUpdateFilters =
    (lookupRefinementMode
      ? permissionsManager.canViewTable(tableId)
      : permissionsManager.canConfigureTableTabs(tableId)) && !readOnly

  // Internal draft state, only include the pending filter item if there is no
  // existing filter item for the same field. We have to do this because we are now
  // extending the grid filter item to include the table id, and MUI won't know how to handle it.
  const [draftModel, setDraftModel] = useState<DossGridFilterModel>(() => {
    if (pendingFilterItem) {
      const existingFilterItem = filterModel.items.find(
        (item) =>
          item.field === pendingFilterItem.field && item.tableId === pendingFilterItem.tableId,
      )
      if (existingFilterItem) {
        return filterModel
      }
      return {
        ...filterModel,
        items: [...filterModel.items, pendingFilterItem],
      }
    }
    return filterModel
  })

  // We support filtering a parent by its child tables, but we intentionally do not offer
  // filtering a child table by its parent's columns. So, we only show the table dropdown (choice
  // of parent and child) when the current/parent table has a child table.
  const showTableDropdown = table.childTables.length > 0

  // Sync draft state when filterModel prop changes
  useEffect(() => {
    setDraftModel(filterModel)
  }, [filterModel])

  // Sync draft state when pendingFilterItem prop changes and the draft model has not been initialized
  useEffect(() => {
    if (pendingFilterItem && !hasInitializedDraftFilterModel) {
      const existingFilterItem = filterModel.items.find(
        (item) =>
          item.field === pendingFilterItem.field && item.tableId === pendingFilterItem.tableId,
      )
      if (!existingFilterItem) {
        const newModel = {
          ...filterModel,
          items: [...filterModel.items, pendingFilterItem],
        }
        setDraftModel(newModel)
        onFilterModelChange(newModel)
      }

      setHasInitializedDraftFilterModel(true)
    }
  }, [pendingFilterItem, hasInitializedDraftFilterModel, filterModel, onFilterModelChange])

  const handleAddFilter = useCallback(() => {
    const firstColumn = first(columns.filter((column) => column.table.id === tableId))
    if (!firstColumn) {
      return
    }

    const operators = getGridFilterOperators(firstColumn, {
      activeUsers,
      includeDuplicateOperator: canDuplicateFilter(firstColumn, tableId, lookupRefinementMode),
    })
    const firstOperator = first(operators)
    const newFilter: DossGridFilterItem = {
      tableId: firstColumn.table.id,
      field: firstColumn.id,
      operator: firstOperator?.value ?? '',
      value: '',
    }

    const newModel = {
      ...draftModel,
      items: [...draftModel.items, newFilter],
      logicOperator:
        draftModel.items.length === 1 ? GridLogicOperator.And : draftModel.logicOperator,
    }

    setDraftModel(newModel)
    onFilterModelChange(newModel)
  }, [activeUsers, columns, draftModel, lookupRefinementMode, onFilterModelChange, tableId])

  const handleRemoveFilter = useCallback(
    (index: number) => {
      const updatedItems = draftModel.items.filter((_, i) => i !== index)
      const newModel = {
        ...draftModel,
        items: updatedItems,
      }
      setDraftModel(newModel)
      onFilterModelChange(newModel)
    },
    [draftModel, onFilterModelChange],
  )

  const handleRemoveAll = useCallback(() => {
    const newModel = {
      ...draftModel,
      items: [],
    }
    setDraftModel(newModel)
    onFilterModelChange(newModel)
  }, [draftModel, onFilterModelChange])

  const handleFilterChange = useCallback(
    (newModel: DossGridFilterModel) => {
      setDraftModel(newModel)
      onFilterModelChange(newModel)
    },
    [onFilterModelChange],
  )

  return (
    <div ref={panelRef} className="flex min-w-[800px] flex-col gap-6">
      <div className="p-5">
        <div className="pb-2 text-xs text-gray-8">
          {lookupRefinementMode ? t('filter.show_records') : t('filter.in_this_view_show_records')}
        </div>
        {/* Cap row width only in the lookup refinement popover, which is much wider than the table
            filter panel; the table panel should keep its natural width. */}
        <div className={cn('flex flex-col gap-4 pt-4', lookupRefinementMode && 'max-w-[720px]')}>
          {filterModel.items.map((item, index) => (
            <FilterRow
              key={index}
              activeUsers={activeUsers}
              columns={columns}
              disableFields={!canUpdateFilters}
              filterModel={filterModel}
              index={index}
              item={item}
              lookupRefinementMode={lookupRefinementMode}
              onFilterModelChange={handleFilterChange}
              onRemove={handleRemoveFilter}
              showLogicOperator={filterModel.items.length > 1}
              showTableDropdown={showTableDropdown}
              table={table}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-between border-t border-gray-4 px-5 py-4">
        <Button
          className="flex items-center gap-1"
          disabled={!canUpdateFilters}
          onClick={handleAddFilter}
          size="sm"
          variant="ghost"
        >
          <Plus className="size-4" />
          {t('filter.add_filter')}
        </Button>
        {filterModel.items.length > 0 && (
          <Button
            className="flex items-center gap-1"
            disabled={!canUpdateFilters}
            onClick={handleRemoveAll}
            size="sm"
            variant="ghost"
          >
            <Trash2 className="size-4" />
            {t('filter.remove_all')}
          </Button>
        )}
      </div>
    </div>
  )
}
